"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsGetAssetDetails = exports.googleAdsGetAssetDetailsResultSchema = exports.googleAdsGetAssetDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var googleAdsGetAssetDetailsInputSchema = zod_1.z
    .object({
    customerId: zod_1.z
        .string()
        .nullish()
        .describe('ONLY provide if user explicitly specifies a Google Ads customer ID. DO NOT generate or guess customer IDs. Leave empty to automatically use the selected account. Must be provided by user - never invent random numbers.'),
    campaignId: zod_1.z
        .string()
        .nullish()
        .describe('Specific Performance Max campaign ID to analyze. If not provided, returns data for all Performance Max campaigns in the account.'),
    assetGroupId: zod_1.z
        .string()
        .nullish()
        .describe('Specific asset group ID to analyze. If not provided, returns data for all asset groups.'),
    assetType: zod_1.z
        .enum(['HEADLINE', 'DESCRIPTION', 'IMAGE', 'VIDEO', 'LOGO', 'ALL'])
        .nullish()
        .default('ALL')
        .describe('Filter by asset type. Defaults to ALL.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-08-01. Optional.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-08-31. Optional.'),
    metrics: zod_1.z
        .array(zod_1.z.enum(['impressions', 'clicks', 'cost', 'conversions', 'ctr', 'cpc', 'cpm']))
        .nullish()
        .default(['impressions', 'clicks', 'cost', 'ctr'])
        .describe('Metrics to include in the response. Defaults to basic metrics.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of assets to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    includeDisabled: zod_1.z
        .boolean()
        .default(false)
        .describe('Include paused, removed, or disabled assets in results. Set to true to show all assets regardless of status. Defaults to false.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if no asset data is found. Unless specified, defaults to true.'),
        groupByDay: zod_1.z
            .boolean()
            .default(false)
            .describe('Group performance data by day. Unless specified, defaults to false.'),
    })
        .describe('Performance query options'),
})
    .describe('Get performance metrics for individual assets (headlines, descriptions, images, videos) in Performance Max campaigns. Shows which specific assets drive conversions.');
exports.googleAdsGetAssetDetailsArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsGetAssetDetailsInputSchema);
var googleAdsGetAssetDetailsResponseSchema = zod_1.z.array(zod_1.z.object({
    assetId: zod_1.z.string().describe('The asset ID'),
    assetName: zod_1.z.string().nullish().describe('The asset name or text content'),
    assetType: zod_1.z
        .enum(['HEADLINE', 'DESCRIPTION', 'IMAGE', 'VIDEO', 'LOGO'])
        .describe('The type of asset'),
    assetGroupId: zod_1.z.string().nullish().describe('The asset group ID this asset belongs to'),
    assetGroupName: zod_1.z.string().nullish().describe('The asset group name'),
    campaignId: zod_1.z.string().nullish().describe('The campaign ID'),
    campaignName: zod_1.z.string().nullish().describe('The campaign name'),
    status: zod_1.z.string().nullish().describe('Asset status'),
    performance: zod_1.z
        .object({
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
        conversions: zod_1.z.number().nullish(),
        ctr: zod_1.z.number().nullish(),
        cpc: zod_1.z.number().nullish(),
        cpm: zod_1.z.number().nullish(),
        conversionRate: zod_1.z.number().nullish(),
    })
        .describe('Performance metrics for the asset'),
    dailyBreakdown: zod_1.z
        .array(zod_1.z.object({
        date: zod_1.z.string(),
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
    }))
        .nullish()
        .describe('Daily performance breakdown if groupByDay is enabled'),
}));
exports.googleAdsGetAssetDetailsResultSchema = googleAdsGetAssetDetailsResponseSchema;
exports.googleAdsGetAssetDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'googleAdsGetAssetDetails',
    displayName: 'Google Ads Asset Performance',
    displayDescription: 'Get performance metrics for individual assets (headlines, descriptions, images, videos) in Performance Max campaigns. Shows which specific assets drive conversions. Automatically uses the selected account unless user provides specific customer ID.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsGetAssetDetailsArgsSchema,
}, googleAdsGetAssetDetailsResponseSchema);
